import { useEffect, useState } from 'react';
import { Bell, Save, Info, AlertCircle } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import SettingsSection from '@/components/calendar/SettingsSection';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { cn } from '@/lib/utils';
import {
  calendarNotificationEventSchema, calendarNotificationRecipientsSchema,
  calendarNotificationSettingsResponseSchema,
} from '@easyflow/shared';
import type {
  CalendarNotificationEvent as EventKey,
  CalendarNotificationRecipients as Recipients,
  CalendarNotificationEventConfig as EventConfig,
  CalendarNotificationSettings as Settings,
  CalendarNotificationSettingsResponse,
} from '@easyflow/shared';

const EVENT_LABELS: Record<EventKey, { label: string; hint: string }> = {
  appointment_created: {
    label: 'Nuovo appuntamento creato',
    hint: 'Quando un appuntamento viene creato (interfaccia, form pubblico, automazione, MCP).',
  },
  appointment_updated: {
    label: 'Appuntamento modificato',
    hint: 'Quando cambia orario, stato (eccetto cancellato), assegnatario o dati cliente.',
  },
  appointment_cancelled: {
    label: 'Appuntamento cancellato',
    hint: 'Quando un appuntamento viene cancellato o eliminato.',
  },
  public_form_submission: {
    label: 'Nuova richiesta da form pubblico',
    hint: 'Quando un cliente compila un form pubblico di prenotazione.',
  },
  hr_availability_conflict: {
    label: 'Appuntamento da gestire per assenza approvata',
    hint: 'Quando un’assenza approvata richiede una verifica degli appuntamenti.',
  },
};

const RECIPIENTS_OPTIONS: { value: Recipients; label: string; hint: string }[] = [
  { value: 'assigned', label: 'Solo consulente assegnato', hint: 'L\'utente assegnato all\'appuntamento.' },
  { value: 'admins', label: 'Tutti gli admin', hint: 'Admin e super_admin del tenant.' },
  { value: 'all', label: 'Tutti gli operatori', hint: 'Tutti gli utenti attivi del tenant.' },
];

const DEFAULT_EVENT: EventConfig = {
  enabled: true,
  channels: { toast: true, badge: true, push: false },
  recipients: 'assigned',
};

const DEFAULT_SETTINGS: Settings = {
  events: {
    appointment_created: { ...DEFAULT_EVENT, recipients: 'assigned' },
    appointment_updated: { ...DEFAULT_EVENT, recipients: 'assigned' },
    appointment_cancelled: { ...DEFAULT_EVENT, recipients: 'assigned' },
    public_form_submission: { ...DEFAULT_EVENT, recipients: 'admins' },
    hr_availability_conflict: { ...DEFAULT_EVENT, channels: { toast: true, badge: true, push: true }, recipients: 'admins' },
  },
};

export default function CalendarNotificationsTab() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  // This route deliberately retains its stricter admin-only write policy.
  const isAdmin = (user?.role === 'admin' || user?.role === 'super_admin') && hasPermission('calendar.manage');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<CalendarNotificationSettingsResponse>(
          '/calendar/notifications/settings',
        );
        if (cancelled) return;
        if (!res.success) throw new Error(res.error || 'Errore caricamento impostazioni');
        setSettings(calendarNotificationSettingsResponseSchema.parse(res.data).settings);
        setLoaded(true);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore caricamento impostazioni');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateEvent = (key: EventKey, patch: Partial<EventConfig>) => {
    setSettings(prev => ({
      ...prev,
      events: {
        ...prev.events,
        [key]: { ...prev.events[key], ...patch },
      },
    }));
  };

  const updateChannel = (
    key: EventKey,
    channel: 'toast' | 'badge' | 'push',
    value: boolean,
  ) => {
    setSettings(prev => ({
      ...prev,
      events: {
        ...prev.events,
        [key]: {
          ...prev.events[key],
          channels: { ...prev.events[key].channels, [channel]: value },
        },
      },
    }));
  };

  const handleSave = async () => {
    if (!isAdmin || !loaded) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient.put<CalendarNotificationSettingsResponse>(
        '/calendar/notifications/settings',
        { settings },
      );
      if (res?.success && res.data?.settings) {
        setSettings(calendarNotificationSettingsResponseSchema.parse(res.data).settings);
        setSavedAt(new Date());
      } else {
        setError(res?.error || 'Salvataggio fallito');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      title="Notifiche interne operatori"
      icon={<Bell className="w-5 h-5 text-purple-500" />}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-900 dark:text-blue-200">
          <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Notifiche interne, non per i clienti.</p>
            <p className="mt-1 text-blue-800/90 dark:text-blue-200/80">
              Queste impostazioni controllano badge, toast in-app e push browser
              ricevuti dagli <strong>operatori del tenant</strong>. Le email/SMS/WhatsApp
              inviate ai clienti si configurano nella tab <strong>Automazioni</strong>.
            </p>
          </div>
        </div>

        {!isAdmin && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-200">
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>Solo gli amministratori del tenant possono modificare questa configurazione. Puoi consultarla in sola lettura.</div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-900 dark:text-red-200">
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">Caricamento…</div>
        ) : (
          <div className="space-y-3">
            {calendarNotificationEventSchema.options.map(key => {
              const cfg = settings.events[key];
              const meta = EVENT_LABELS[key];
              return (
                <div
                  key={key}
                  className={cn(
                    'rounded-lg border p-4 transition-colors',
                    cfg.enabled
                      ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40'
                      : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 opacity-75',
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">{meta.label}</h4>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{meta.hint}</p>
                    </div>
                    <Switch
                      checked={cfg.enabled}
                      disabled={!isAdmin}
                      onCheckedChange={v => updateEvent(key, { enabled: !!v })}
                    />
                  </div>

                  {cfg.enabled && (
                    <div className="mt-4 grid gap-4 lg:grid-cols-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Canali di notifica
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <ChannelChip
                            label="Toast in-app"
                            active={cfg.channels.toast}
                            disabled={!isAdmin}
                            onChange={v => updateChannel(key, 'toast', v)}
                          />
                          <ChannelChip
                            label="Badge contatore"
                            active={cfg.channels.badge}
                            disabled={!isAdmin}
                            onChange={v => updateChannel(key, 'badge', v)}
                          />
                          <ChannelChip
                            label="Push browser"
                            active={cfg.channels.push}
                            disabled={!isAdmin}
                            onChange={v => updateChannel(key, 'push', v)}
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor={`calendar-notification-recipients-${key}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Destinatari
                        </label>
                        <Select
                          value={cfg.recipients}
                          disabled={!isAdmin}
                          onValueChange={v => updateEvent(key, { recipients: calendarNotificationRecipientsSchema.parse(v) })}
                        >
                          <SelectTrigger id={`calendar-notification-recipients-${key}`} className="w-full text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RECIPIENTS_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {RECIPIENTS_OPTIONS.find(o => o.value === cfg.recipients)?.hint}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {isAdmin && (
              <div className="flex items-center justify-end gap-3 pt-2">
                {savedAt && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Salvato alle {savedAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                )}
                <Button onClick={handleSave} disabled={saving || !loaded}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Salvataggio…' : 'Salva impostazioni'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </SettingsSection>
  );
}

function ChannelChip({
  label,
  active,
  disabled,
  onChange,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!active)}
      className={cn(
        'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
        active
          ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-300 dark:border-purple-700 text-purple-900 dark:text-purple-200'
          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {active ? '✓ ' : ''}{label}
    </button>
  );
}
